import {
  FileIcon,
  FileTextIcon,
  ImageIcon,
  Loader2Icon,
  PresentationIcon,
  SheetIcon,
  SparklesIcon,
} from 'lucide-react';
import type { DocumentGenerationKindDTO } from '@shared/schemas/document-generation';
import type { ChatDocumentDTO } from '@shared/schemas/chat';
import { cn } from '@/lib/utils';
import {
  DOCX_MIME_TYPE,
  PDF_MIME_TYPE,
  PPTX_MIME_TYPE,
  XLSX_MIME_TYPE,
} from './document-viewer/document-viewer-registry';
import { DocxFileKindIcon, PdfFileKindIcon } from './DocumentGenerationKindIcons';

type ChatDocumentKindIconProps = {
  document: Pick<ChatDocumentDTO, 'kind' | 'mediaType' | 'status' | 'filename'>;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
};

const SIZE_CLASS = {
  xs: {
    container: 'size-7 rounded-lg',
    svg: 'size-5',
    lucide: 'size-3.5',
  },
  sm: {
    container: 'size-9 rounded-xl',
    svg: 'size-6',
    lucide: 'size-4',
  },
  md: {
    container: 'size-11 rounded-xl',
    svg: 'size-7',
    lucide: 'size-5',
  },
} as const;

// Helper interne (non exporté) : garder ce fichier « composants only » pour Fast Refresh
// (react-refresh/only-export-components). Aucun consommateur externe.
function resolveDocumentGenerationKind(
  document: Pick<ChatDocumentDTO, 'mediaType' | 'filename'>,
): DocumentGenerationKindDTO | null {
  const mediaType = document.mediaType?.toLowerCase();

  if (mediaType === PDF_MIME_TYPE) {
    return 'pdf';
  }

  if (mediaType === DOCX_MIME_TYPE) {
    return 'docx';
  }

  if (mediaType === XLSX_MIME_TYPE) {
    return 'xlsx';
  }

  if (mediaType === PPTX_MIME_TYPE) {
    return 'pptx';
  }

  const filename = document.filename?.toLowerCase() ?? '';

  if (filename.endsWith('.pdf')) {
    return 'pdf';
  }

  if (filename.endsWith('.docx')) {
    return 'docx';
  }

  if (filename.endsWith('.xlsx')) {
    return 'xlsx';
  }

  if (filename.endsWith('.pptx')) {
    return 'pptx';
  }

  return null;
}

export function DocumentGenerationKindIcon({
  kind,
  size = 'md',
  className,
}: {
  kind: DocumentGenerationKindDTO | null;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
}) {
  const sizeClass = SIZE_CLASS[size];

  if (kind === 'docx') {
    return (
      <div
        className={cn(
          'flex shrink-0 items-center justify-center bg-blue-600/5',
          sizeClass.container,
          className,
        )}
        title="Word"
      >
        <DocxFileKindIcon className={sizeClass.svg} />
      </div>
    );
  }

  if (kind === 'pdf') {
    return (
      <div
        className={cn(
          'flex shrink-0 items-center justify-center bg-rose-500/5',
          sizeClass.container,
          className,
        )}
        title="PDF"
      >
        <PdfFileKindIcon className={sizeClass.svg} />
      </div>
    );
  }

  if (kind === 'xlsx') {
    return (
      <div
        className={cn(
          'flex shrink-0 items-center justify-center bg-emerald-500/5 text-emerald-700 dark:text-emerald-300',
          sizeClass.container,
          className,
        )}
        title="Tableur"
      >
        <SheetIcon className={sizeClass.lucide} aria-hidden />
      </div>
    );
  }

  if (kind === 'pptx') {
    return (
      <div
        className={cn(
          'flex shrink-0 items-center justify-center bg-amber-500/5 text-amber-800 dark:text-amber-300',
          sizeClass.container,
          className,
        )}
        title="Présentation"
      >
        <PresentationIcon className={sizeClass.lucide} aria-hidden />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center bg-muted/35 text-muted-foreground',
        sizeClass.container,
        className,
      )}
      title="Document"
    >
      <FileTextIcon className={sizeClass.lucide} aria-hidden />
    </div>
  );
}

export function ChatDocumentKindIcon({
  document,
  size = 'md',
  className,
}: ChatDocumentKindIconProps) {
  const sizeClass = SIZE_CLASS[size];

  if (document.status === 'streaming') {
    return (
      <div
        className={cn(
          'flex shrink-0 items-center justify-center bg-muted/35',
          sizeClass.container,
          className,
        )}
      >
        <Loader2Icon className={cn(sizeClass.lucide, 'animate-spin text-muted-foreground')} aria-hidden />
      </div>
    );
  }

  // Résoudre le type de document (Word/PDF/tableur/présentation) en priorité, y
  // compris pour les documents générés exposés comme « attachment » : on affiche
  // alors la même icône branded que la carte de la conversation.
  const generationKind = resolveDocumentGenerationKind(document);
  if (generationKind) {
    return (
      <DocumentGenerationKindIcon
        className={className}
        kind={generationKind}
        size={size}
      />
    );
  }

  if (document.mediaType?.startsWith('image/')) {
    return (
      <div
        className={cn(
          'flex shrink-0 items-center justify-center bg-muted/35 text-muted-foreground',
          sizeClass.container,
          className,
        )}
        title="Image"
      >
        <ImageIcon className={sizeClass.lucide} aria-hidden />
      </div>
    );
  }

  if (document.kind === 'assistant-brief') {
    return (
      <div
        className={cn(
          'flex shrink-0 items-center justify-center bg-muted/35 text-muted-foreground',
          sizeClass.container,
          className,
        )}
        title="Brief assistant"
      >
        <SparklesIcon className={sizeClass.lucide} aria-hidden />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center bg-muted/35 text-muted-foreground',
        sizeClass.container,
        className,
      )}
      title="Pièce jointe"
    >
      <FileIcon className={sizeClass.lucide} aria-hidden />
    </div>
  );
}
